import React from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  formatActive,
  formatCount,
  formatDate,
  formatGraduationRate,
  hasProgressData,
  SUPPRESSED_HINT,
} from './format';

const STATUS_STYLES = {
  live: 'bg-[#E6F5F5] text-persian-600',
  ended: 'bg-gray-100 text-gray-600',
  upcoming: 'bg-[#FBEFF3] text-claret-500',
};

function StatusPill({ status }) {
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-[11px] capitalize ${
        STATUS_STYLES[status] || STATUS_STYLES.ended
      }`}
    >
      {status}
    </span>
  );
}

function Count({ value }) {
  return (
    <span title={value === null ? SUPPRESSED_HINT : undefined}>
      {formatCount(value)}
    </span>
  );
}

// Rows are years (kind="year") or cohorts (kind="cohort"); clicking a row
// drills into it.
function LevelTable({ kind, rows, withoutProgress, onSelect }) {
  const isYear = kind === 'year';

  return (
    <div className="overflow-x-auto -mx-5">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 bg-gray-50 border-y border-gray-200">
            <th className="py-2 pl-5 pr-2 font-medium">
              {isYear ? 'Year' : 'Cohort'}
            </th>
            <th className="py-2 px-2 font-medium">
              {isYear ? 'Cohorts' : 'Dates'}
            </th>
            {!isYear && <th className="py-2 px-2 font-medium">Status</th>}
            <th className="py-2 px-2 font-medium text-right">Enrolled</th>
            <th className="py-2 px-2 font-medium text-right">Active</th>
            <th className="py-2 px-2 font-medium text-right">Graduated</th>
            <th className="py-2 px-2 font-medium text-right">Grad. rate</th>
            <th className="py-2 pr-5 w-8" aria-label="Open" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={isYear ? row.year : row.id}
              onClick={() => onSelect(isYear ? row.year : row.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelect(isYear ? row.year : row.id);
                }
              }}
              tabIndex={0}
              className="group border-b border-gray-100 last:border-0 cursor-pointer hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50"
            >
              <td className="py-3 pl-5 pr-2 font-semibold text-gray-900 group-hover:text-claret-500">
                {isYear ? row.year : row.name}
              </td>
              <td className="py-3 px-2 text-gray-600 text-xs whitespace-nowrap">
                {isYear
                  ? `${row.cohorts?.total ?? 0}${
                      row.cohorts?.live ? ` · ${row.cohorts.live} live` : ''
                    }`
                  : `${formatDate(row.start_date)} – ${formatDate(
                      row.end_date
                    )}`}
              </td>
              {!isYear && (
                <td className="py-3 px-2">
                  <StatusPill status={row.status} />
                </td>
              )}
              <td className="py-3 px-2 text-right tabular-nums text-gray-700">
                <Count value={row.enrolled} />
              </td>
              <td className="py-3 px-2 text-right tabular-nums text-gray-700">
                {formatActive(row, hasProgressData(row, withoutProgress))}
              </td>
              <td className="py-3 px-2 text-right tabular-nums text-gray-700">
                <Count value={row.graduated} />
              </td>
              <td className="py-3 px-2 text-right tabular-nums font-semibold text-gray-900">
                {formatGraduationRate(row)}
              </td>
              <td className="py-3 pr-5 text-gray-300 group-hover:text-claret-500">
                <ChevronRightIcon fontSize="small" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export { StatusPill };
export default LevelTable;
